import React, { useState, useEffect } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import SearchInput from "@/components/SearchInput/SearchInput";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LibraryModuleListFilters } from "@/modules/learningCenter/features/ModuleLibrary/hooks/useLibraryModuleList/useLibraryModuleList";
import { LIBRARY_MODULE_DIFFICULTY_OPTIONS } from "@/modules/learningCenter/features/ModuleLibrary/types/libraryModule";

interface LibraryModuleFiltersProps {
  filters: LibraryModuleListFilters;
  onFilterChange: (
    filterKey: keyof LibraryModuleListFilters,
    value: string,
  ) => void;
}

const ALL_VALUE = "__all__";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
];

const LibraryModuleFilters: React.FC<LibraryModuleFiltersProps> = ({
  filters,
  onFilterChange,
}) => {
  const [searchInput, setSearchInput] = useState(filters.search);
  const debouncedSearch = useDebouncedValue(searchInput, 500);

  const [tagInput, setTagInput] = useState(filters.tag);
  const debouncedTag = useDebouncedValue(tagInput, 500);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      onFilterChange("search", debouncedSearch);
    }
  }, [debouncedSearch, filters.search, onFilterChange]);

  useEffect(() => {
    if (debouncedTag !== filters.tag) {
      onFilterChange("tag", debouncedTag);
    }
  }, [debouncedTag, filters.tag, onFilterChange]);

  return (
    <div className="mb-4 space-y-4">
      <SearchInput
        onChange={(val) => setSearchInput(val)}
        placeholder="Search modules by title..."
        value={searchInput}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="mb-1 block text-sm text-gray-500 dark:text-gray-400">
            Status
          </label>
          <Select
            onValueChange={(val) =>
              onFilterChange("status", val === ALL_VALUE ? "" : val)
            }
            value={filters.status || ALL_VALUE}
          >
            <SelectTrigger aria-label="Filter by status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All statuses</SelectItem>
              {STATUS_OPTIONS.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-sm text-gray-500 dark:text-gray-400">
            Difficulty
          </label>
          <Select
            onValueChange={(val) =>
              onFilterChange("difficulty", val === ALL_VALUE ? "" : val)
            }
            value={filters.difficulty || ALL_VALUE}
          >
            <SelectTrigger aria-label="Filter by difficulty">
              <SelectValue placeholder="All difficulties" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>All difficulties</SelectItem>
              {LIBRARY_MODULE_DIFFICULTY_OPTIONS.map((difficulty) => (
                <SelectItem key={difficulty} value={difficulty}>
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-sm text-gray-500 dark:text-gray-400">
            Tag
          </label>
          <Input
            aria-label="Filter by tag"
            onChange={(e) => setTagInput(e.target.value)}
            placeholder="Filter by tag..."
            value={tagInput}
          />
        </div>
      </div>
    </div>
  );
};

export default LibraryModuleFilters;
